import {
    getFilledFailListItems,
    normalizeAudioControlBlocks,
    normalizeFailList,
    type AudioControlBlock,
    type AudioControlFailListItem,
} from "../../Settings/AudioControlList/audioControlQuestionnaire";

export type QualityControlSimpleOption = {
    id: number;
    name: string;
    status: number | null;
    statusLabel: string;
    statusBadgeClassName: string;
    searchLabel: string;
    score: number | null;
    isManual: boolean | null;
    userFullName: string | null;
    dateTimeEnd: string | null;
    isArchived: boolean;
};

export const QUALITY_CONTROL_FORM_STATUS = {
    NOT_STARTED: 100,
    IN_PROGRESS: 101,
    EVALUATING: 102,
    COMPLETED: 103,
} as const;

export type QuestionnaireStatusMeta = {
    label: string;
    badgeClassName: string;
};

export const QUESTIONNAIRE_STATUS_BADGE_CLASS = {
    NOT_EVALUATED: 'manual-evaluation-status-badge--not-evaluated',
    IN_PROGRESS: 'manual-evaluation-status-badge--in-progress',
    EVALUATED: 'manual-evaluation-status-badge--evaluated',
} as const;

export const getQuestionnaireStatusMeta = (
    status: unknown,
    options?: { isRated?: boolean },
): QuestionnaireStatusMeta => {
    if (status == null || status === '') {
        return {
            label: 'Не оцінено',
            badgeClassName: QUESTIONNAIRE_STATUS_BADGE_CLASS.NOT_EVALUATED,
        };
    }

    const parsed = Number(status);
    if (!Number.isFinite(parsed)) {
        return {
            label: 'Не оцінено',
            badgeClassName: QUESTIONNAIRE_STATUS_BADGE_CLASS.NOT_EVALUATED,
        };
    }

    switch (parsed) {
        case QUALITY_CONTROL_FORM_STATUS.NOT_STARTED:
            return {
                label: 'Не оцінено',
                badgeClassName: QUESTIONNAIRE_STATUS_BADGE_CLASS.NOT_EVALUATED,
            };
        case QUALITY_CONTROL_FORM_STATUS.IN_PROGRESS:
            return {
                label: 'В процесі',
                badgeClassName: QUESTIONNAIRE_STATUS_BADGE_CLASS.IN_PROGRESS,
            };
        case QUALITY_CONTROL_FORM_STATUS.EVALUATING:
            return {
                label: 'Оцінюється',
                badgeClassName: QUESTIONNAIRE_STATUS_BADGE_CLASS.IN_PROGRESS,
            };
        case QUALITY_CONTROL_FORM_STATUS.COMPLETED:
            return {
                label: 'Оцінено',
                badgeClassName: QUESTIONNAIRE_STATUS_BADGE_CLASS.EVALUATED,
            };
        default:
            return {
                label: `Статус ${parsed}`,
                badgeClassName: QUESTIONNAIRE_STATUS_BADGE_CLASS.NOT_EVALUATED,
            };
    }
};

export const formatQuestionnaireSearchLabel = (id: number, name: string, statusLabel?: string) => {
    const base = `${id} ${name}`.trim();

    if (!statusLabel) {
        return base;
    }

    return `${base} ${statusLabel}`.trim();
};

export const toEvaluatedQuestionnaireOption = (
    option: QualityControlSimpleOption,
): QualityControlSimpleOption => {
    const statusMeta = getQuestionnaireStatusMeta(QUALITY_CONTROL_FORM_STATUS.COMPLETED, {
        isRated: true,
    });

    return {
        ...option,
        status: QUALITY_CONTROL_FORM_STATUS.COMPLETED,
        statusLabel: statusMeta.label,
        statusBadgeClassName: statusMeta.badgeClassName,
        searchLabel: formatQuestionnaireSearchLabel(option.id, option.name),
        score: option.score,
        isManual: option.isManual,
        userFullName: option.userFullName,
        dateTimeEnd: option.dateTimeEnd,
    };
};

const parseQuestionnaireNumber = (value: unknown): number | null => {
    if (value == null || value === '') {
        return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

const parseQuestionnaireStatus = (row: Record<string, unknown>): number | null =>
    parseQuestionnaireNumber(row.status ?? row.Status);

export const normalizeSimpleUsersControls = (
    data: unknown,
    options?: { isRated?: boolean },
): QualityControlSimpleOption[] => {
    if (!Array.isArray(data)) {
        return [];
    }

    return data
        .map((item) => {
            const row = (item ?? {}) as Record<string, unknown>;
            const id = Number(row.id ?? row.Id);
            const name = String(row.name ?? row.Name ?? '').trim();
            const status = parseQuestionnaireStatus(row);
            const statusMeta = getQuestionnaireStatusMeta(status, options);
            const userFullName = String(
                row.createdByUserFullName
                ?? row.CreatedByUserFullName
                ?? row.userFullName
                ?? row.UserFullName
                ?? ''
            ).trim();
            const dateTimeEnd = row.dateTimeEnd ?? row.DateTimeEnd;
            const isManualRaw = row.isManual ?? row.IsManual;
            const isManual = isManualRaw === true || isManualRaw === 'true' || isManualRaw === 1 || isManualRaw === '1'
                ? true
                : isManualRaw === false || isManualRaw === 'false' || isManualRaw === 0 || isManualRaw === '0'
                    ? false
                    : null;
            const isArchivedRaw = row.isArchived ?? row.IsArchived;

            return {
                id,
                name,
                status,
                statusLabel: statusMeta.label,
                statusBadgeClassName: statusMeta.badgeClassName,
                searchLabel: formatQuestionnaireSearchLabel(id, name),
                score: parseQuestionnaireNumber(row.score ?? row.Score),
                isManual,
                userFullName: userFullName || null,
                dateTimeEnd: dateTimeEnd == null || dateTimeEnd === '' ? null : String(dateTimeEnd),
                isArchived: isArchivedRaw === true || isArchivedRaw === 'true' || isArchivedRaw === 1 || isArchivedRaw === '1',
            };
        })
        .filter((item) => Number.isFinite(item.id) && item.id > 0 && item.name.length > 0);
};

export const normalizeAudioControlDetails = (data: unknown): {
    blocks: AudioControlBlock[];
    failList: AudioControlFailListItem[];
} => {
    if (data == null || typeof data !== 'object' || Array.isArray(data)) {
        return {
            blocks: [],
            failList: [],
        };
    }

    const row = data as Record<string, unknown>;

    return {
        blocks: normalizeAudioControlBlocks(row.blocks ?? row.models),
        failList: getFilledFailListItems(
            normalizeFailList(row.failList ?? row.questionnaireFailConditions)
        ),
    };
};
